import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientMedication, CreateMedicationRequest } from '@/types/patient.types';

const medicationSchema = z.object({
  medicationName: z.string().min(1, 'Nome do medicamento é obrigatório'),
  activeIngredient: z.string().optional(),
  dosage: z.string().min(1, 'Dosagem é obrigatória'),
  frequency: z.string().min(1, 'Frequência é obrigatória'),
  route: z.string().optional(),
  startDate: z.string().optional(),
  notes: z.string().optional(),
});

type MedicationFormData = z.infer<typeof medicationSchema>;

interface AddMedicationDialogProps {
  patientId: string;
  onSuccess: (medication: PatientMedication) => void;
}

const ROUTES = [
  { value: 'ORAL', label: 'Via Oral' },
  { value: 'SUBLINGUAL', label: 'Sublingual' },
  { value: 'INTRAVENOUS', label: 'Intravenosa' },
  { value: 'INTRAMUSCULAR', label: 'Intramuscular' },
  { value: 'SUBCUTANEOUS', label: 'Subcutânea' },
  { value: 'TOPICAL', label: 'Tópica' },
  { value: 'INHALATION', label: 'Inalatória' },
  { value: 'RECTAL', label: 'Retal' },
  { value: 'OPHTHALMIC', label: 'Oftálmica' },
  { value: 'NASAL', label: 'Nasal' },
  { value: 'OTHER', label: 'Outra' },
];

export function AddMedicationDialog({ patientId, onSuccess }: AddMedicationDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MedicationFormData>({
    resolver: zodResolver(medicationSchema),
    defaultValues: {
      medicationName: '',
      activeIngredient: '',
      dosage: '',
      frequency: '',
      route: '',
      startDate: '',
      notes: '',
    },
  });

  const onSubmit = async (data: MedicationFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateMedicationRequest = {
        medicationName: data.medicationName,
        dosage: data.dosage,
        frequency: data.frequency,
        activeIngredient: data.activeIngredient || undefined,
        route: selectedRoute || undefined,
        startDate: data.startDate || undefined,
        notes: data.notes || undefined,
      };

      const medication = await patientsApi.addMedication(patientId, request);
      onSuccess(medication);
      reset();
      setSelectedRoute('');
      setOpen(false);
    } catch (err: unknown) {
      console.error('Erro ao adicionar medicamento:', err);

      // Verifica se é erro de permissão (403 Forbidden)
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 403) {
        setError('Você não tem permissão para adicionar medicamentos. Esta ação é restrita a médicos.');
      } else {
        setError('Erro ao adicionar medicamento. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRouteChange = (value: string) => {
    setSelectedRoute(value);
    setValue('route', value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Medicamento
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Adicionar Medicamento</DialogTitle>
          <DialogDescription>
            Adicione um novo medicamento à lista de medicamentos em uso do paciente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="medicationName">Nome do Medicamento *</Label>
            <Input
              id="medicationName"
              placeholder="Ex: Losartana Potássica"
              {...register('medicationName')}
            />
            {errors.medicationName && (
              <p className="text-sm text-destructive">{errors.medicationName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="activeIngredient">Princípio Ativo</Label>
            <Input
              id="activeIngredient"
              placeholder="Ex: Losartana"
              {...register('activeIngredient')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dosage">Dosagem *</Label>
              <Input
                id="dosage"
                placeholder="Ex: 50mg"
                {...register('dosage')}
              />
              {errors.dosage && (
                <p className="text-sm text-destructive">{errors.dosage.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Frequência *</Label>
              <Input
                id="frequency"
                placeholder="Ex: 1x ao dia"
                {...register('frequency')}
              />
              {errors.frequency && (
                <p className="text-sm text-destructive">{errors.frequency.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="route">Via de Administração</Label>
              <Select value={selectedRoute} onValueChange={handleRouteChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a via" />
                </SelectTrigger>
                <SelectContent>
                  {ROUTES.map((route) => (
                    <SelectItem key={route.value} value={route.value}>
                      {route.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Data de Início</Label>
              <Input
                id="startDate"
                type="date"
                {...register('startDate')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              placeholder="Observações sobre o uso do medicamento..."
              {...register('notes')}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
